import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { NORDIC_HUB } from "@/data/nordicHub";

/**
 * Nordic Sustainability Hub — /regions/nordic-sustainable-sourcing
 * Shared cross-country landing for Sweden / Denmark / Norway / Finland
 * buyers evaluating India as a certified sustainable sourcing origin.
 */
export const NordicHub = () => {
  const page = NORDIC_HUB;
  const { open } = useStartProject();

  useSeo({
    title: page.title,
    description: page.metaDescription,
    path: page.slug,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${SITE_URL}${page.slug}#service`,
          name: page.h1,
          description: page.metaDescription,
          provider: { "@id": `${SITE_URL}#organization` },
          areaServed: [
            { "@type": "Country", name: "Sweden" },
            { "@type": "Country", name: "Denmark" },
            { "@type": "Country", name: "Norway" },
            { "@type": "Country", name: "Finland" },
          ],
          serviceType:
            "India sourcing · FSC · GOTS · GRS · OEKO-TEX · Fairtrade · Nordic Swan Ecolabel",
          url: `${SITE_URL}${page.slug}`,
        },
        {
          "@type": "FAQPage",
          "@id": `${SITE_URL}${page.slug}#faq`,
          mainEntity: page.faqs.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Regions", path: "/regions" },
          { name: "Nordic sustainable sourcing", path: page.slug },
        ]),
      ],
    },
    jsonLdId: "nordic-hub-jsonld",
  });

  return (
    <article data-testid="nordic-hub-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }} data-testid="nordic-hub-crumb-home">
              Home
            </Link>{" "}
            ·{" "}
            <Link to="/regions" style={{ color: "inherit" }} data-testid="nordic-hub-crumb-regions">
              Regions
            </Link>{" "}
            · Nordic sustainable sourcing
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="nordic-hub-eyebrow">
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="nordic-hub-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="nordic-hub-lede"
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", marginTop: "2rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="nordic-hub-cta-primary"
          >
            Start Your Project →
          </button>
          <Link to="/regions" className="btn btn-ghost" data-testid="nordic-hub-cta-regions">
            See all regions
          </Link>
        </Reveal>
      </header>

      {/* KEY FACTS */}
      <section
        className="wrap"
        data-testid="nordic-hub-facts"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {page.keyFacts.map(([k, v], i) => (
            <Reveal key={i}>
              <span
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.35,
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY sections */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {page.sections.map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
            data-testid={`nordic-hub-section-${i}`}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {s.body.map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
          </Reveal>
        ))}

        {/* Related services */}
        <Reveal as="section" style={{ marginTop: "2.6rem" }} data-testid="nordic-hub-related-services">
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
            }}
          >
            Programme services for Nordic buyers
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {page.relatedServices.map((r, i) => (
              <Link
                key={i}
                to={r.slug}
                data-testid={`nordic-hub-service-${i}`}
                style={{
                  display: "block",
                  padding: "1.2rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .4rem",
                  }}
                >
                  Service
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  {r.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>

        {/* Related Nordic countries */}
        <Reveal as="section" style={{ marginTop: "2.6rem" }} data-testid="nordic-hub-related-countries">
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
            }}
          >
            Nordic country programmes
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {page.relatedCountries.map((c, i) => (
              <Link
                key={i}
                to={c.slug}
                data-testid={`nordic-hub-country-${i}`}
                style={{
                  display: "block",
                  padding: "1.2rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .4rem",
                  }}
                >
                  Country programme
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  India sourcing · {c.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        data-testid="nordic-hub-faq"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.01em",
          }}
        >
          Frequently asked questions — Nordic sustainable sourcing
        </h2>
        {page.faqs.map(([q, a], i) => (
          <details
            key={i}
            data-testid={`nordic-hub-faq-${i}`}
            style={{
              borderTop: i === 0 ? "1px solid var(--line)" : "none",
              borderBottom: "1px solid var(--line)",
              padding: "1.2rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                letterSpacing: "-.005em",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: ".8rem 0 0",
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </details>
        ))}
      </section>

      {/* Final CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="nordic-hub-enquiry"
      >
        <Reveal as="p" className="eyebrow">
          Start an enquiry
        </Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          Nordic sustainable-sourcing brief — talk to Asia Sourcing.
        </Reveal>
        <Reveal
          as="div"
          style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="nordic-hub-cta-enquiry"
          >
            Start Your Project →
          </button>
          <Link to="/contact" className="btn btn-ghost" data-testid="nordic-hub-cta-contact">
            Send an enquiry
          </Link>
        </Reveal>
      </section>
    </article>
  );
};

export default NordicHub;
